import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import "./barcodeEntry.css";
import { useProductContext } from "../../context/ProductContext";
import { useBarcodeEntryContext } from "../../context/BarcodeEntryContext";
import { useShelfContext } from "../../context/ShelfContext";

const BarcodeEntry = () => {
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannedItems, setScannedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedShelves, setSelectedShelves] = useState([]);
  const [shelvesConfirmed, setShelvesConfirmed] = useState(false); // حالة تأكيد الرفوف
  const [barcodeEnabled, setBarcodeEnabled] = useState(false); // حالة تفعيل الباركود
  const inputRef = useRef(null);
  const typingTimerRef = useRef(null);

  const { getProductByBarcode } = useProductContext();
  const { shelves } = useShelfContext();
  const {
    addBarcodeEntry,
    getBarcodeEntries,
    updateBarcodeQuantity,
    deleteBarcodeEntry,
    clearAllEntries,
    confirmEntries,
  } = useBarcodeEntryContext();

  // جلب البيانات المحفوظة عند تحميل الصفحة
  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    const result = await getBarcodeEntries();
    if (result.success && result.data.length > 0) {
      const loadedItems = result.data.map((entry) => ({
        id: entry.id,
        product_id: entry.product_id,
        product_name: entry.product_name,
        barcode: entry.barcode,
        quantity: entry.quantity,
        scannedAt: new Date(entry.scanned_at).toLocaleString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        entryDate: new Date(entry.entry_date).toLocaleString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        last_sale_price: entry.last_sale_price || 0,
        last_purchase_price: entry.last_purchase_price || 0,
      }));
      setScannedItems(loadedItems);
    }
  };

  // التركيز التلقائي على حقل الإدخال (فقط عندما يكون مفعل)
  useEffect(() => {
    if (barcodeEnabled) {
      inputRef.current?.focus();
    }
  }, [barcodeEnabled]);

  // إرجاع الفوكس بعد أي تحديث (فقط عندما يكون مفعل)
  useEffect(() => {
    if (!loading && barcodeEnabled) {
      inputRef.current?.focus();
    }
  }, [scannedItems, loading, barcodeEnabled]);

  // التركيز على الحقل عند النقر في أي مكان (فقط عندما يكون مفعل)
  useEffect(() => {
    const handleGlobalClick = () => {
      if (barcodeEnabled) {
        inputRef.current?.focus();
      }
    };

    const handleContextMenu = () => {
      if (barcodeEnabled) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    };

    document.addEventListener("click", handleGlobalClick);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("click", handleGlobalClick);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [barcodeEnabled]);

  // معالجة التغيير في حقل الإدخال
  useEffect(() => {
    if (!barcodeInput.trim()) return;

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    typingTimerRef.current = setTimeout(() => {
      handleBarcodeAdd(barcodeInput.trim());
    }, 100);

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [barcodeInput]);

  const handleBarcodeAdd = async (barcode) => {
    if (!barcode || barcode.length < 2) {
      setBarcodeInput("");
      return;
    }

    console.log("🔍 البحث عن باركود:", barcode);
    setLoading(true);

    try {
      // البحث عن المنتج
      const product = await getProductByBarcode(barcode);

      if (!product) {
        console.log("❌ المنتج غير موجود");
        alert("المنتج غير موجود في قاعدة البيانات");
        setBarcodeInput("");
        setLoading(false);
        return;
      }

      // 🔐 لا يمكن إدخال باركود لصنف يخص فرعاً آخر
      if (product.isMine === false) {
        alert("هذا الصنف يخص فرعاً آخر. يمكنك عرضه فقط.");
        setBarcodeInput("");
        setLoading(false);
        return;
      }

      console.log("✅ تم العثور على المنتج:", product.name);

      // إضافة مباشرة لقاعدة البيانات
      const result = await addBarcodeEntry(product.id, barcode);

      if (result.success) {
        console.log("✅ تم الحفظ:", result.data.action);

        // تحديث القائمة من قاعدة البيانات
        await loadEntries();
      } else {
        alert("فشل في حفظ الباركود");
      }

      setBarcodeInput("");
    } catch (err) {
      console.error("❌ خطأ:", err);
      alert("حدث خطأ أثناء معالجة الباركود");
      setBarcodeInput("");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuantityChange = async (id, newQuantity) => {
    if (newQuantity < 1) return;

    const numQuantity = parseInt(newQuantity);

    // تحديث محلياً
    setScannedItems(
      scannedItems.map((item) =>
        item.id === id ? { ...item, quantity: numQuantity } : item
      )
    );

    // تحديث في قاعدة البيانات
    await updateBarcodeQuantity(id, numQuantity);
  };

  const handleRemoveItem = async (id) => {
    // حذف من القائمة
    setScannedItems(scannedItems.filter((item) => item.id !== id));

    // حذف من قاعدة البيانات
    const result = await deleteBarcodeEntry(id);
    if (!result.success) {
      alert("فشل في حذف الإدخال");
      // إرجاع البيانات
      await loadEntries();
    }
  };

  const handleCancel = async () => {
    if (scannedItems.length === 0) return;

    if (window.confirm("هل أنت متأكد من حذف جميع الإدخالات؟")) {
      setScannedItems([]);
      setBarcodeInput("");

      // مسح من قاعدة البيانات
      await clearAllEntries();

      if (barcodeEnabled) {
        inputRef.current?.focus();
      }
    }
  };

  // حفظ الرفوف وتفعيل الباركود
  const handleSaveShelves = () => {
    setShelvesConfirmed(true);
    setBarcodeEnabled(true);
  };

  // تعديل الرفوف وتعطيل الباركود
  const handleEditShelves = () => {
    setShelvesConfirmed(false);
    setBarcodeEnabled(false);
  };

  const handleConfirm = async () => {
    if (scannedItems.length === 0) {
      alert("لا توجد إدخالات للتأكيد");
      return;
    }

    const totalItems = scannedItems.length;
    const totalQuantity = scannedItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    if (
      !window.confirm(
        `هل أنت متأكد من تأكيد الإدخال؟\n\n` +
          `عدد الأصناف: ${totalItems}\n` +
          `الكمية الإجمالية: ${totalQuantity}\n\n` +
          `سيتم إضافة الكميات للمخزون وحذف الإدخالات`
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      // إرسال الرفوف المختارة
      const shelf_ids = selectedShelves.map((s) => s.value);
      const result = await confirmEntries(shelf_ids);

      if (result.success) {
        alert(
          `✅ ${result.message}\n\n` +
            `عدد الأصناف: ${result.data.total_items}\n` +
            `الكمية المضافة: ${result.data.total_quantity}`
        );

        // مسح القائمة والرجوع لاختيار الرفوف
        setScannedItems([]);
        setBarcodeInput("");
        setSelectedShelves([]);
        setShelvesConfirmed(false);
        setBarcodeEnabled(false);

        // تحديث من قاعدة البيانات
        await loadEntries();
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (err) {
      console.error("خطأ:", err);
      alert("❌ حدث خطأ أثناء تأكيد الإدخالات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="barcode-entry-container">
      <div className="wrapper">
        <div className="page-header">
          <h1>إدخال من الباركود</h1>
        </div>

        {/* Shelf Selection */}
        <div className="input-section">
          <div className="barcode-form">
            <div className="input-wrapper">
              <label htmlFor="shelves">اختر الرفوف (اختياري)</label>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Select
                    isMulti
                    options={shelves.map((shelf) => ({
                      value: shelf.id,
                      label: shelf.name,
                    }))}
                    value={selectedShelves}
                    onChange={setSelectedShelves}
                    placeholder="اختر الرفوف..."
                    isDisabled={shelvesConfirmed}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "45px",
                        fontSize: "16px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 55,
                      }),
                    }}
                    filterOption={(option, inputValue) => {
                      if (!inputValue) return true;
                      const searchWords = inputValue
                        .toLowerCase()
                        .split(" ")
                        .filter((w) => w);
                      const optionText = option.label.toLowerCase();
                      return searchWords.every((word) =>
                        optionText.includes(word)
                      );
                    }}
                  />
                </div>
                {!shelvesConfirmed ? (
                  <button
                    onClick={handleSaveShelves}
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      minHeight: "45px",
                    }}
                  >
                    حفظ
                  </button>
                ) : (
                  <button
                    onClick={handleEditShelves}
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#f59e0b",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      minHeight: "45px",
                    }}
                  >
                    تعديل
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Barcode Input */}
        <div className="input-section">
          <div className="barcode-form">
            <div className="input-wrapper">
              <label htmlFor="barcode">
                مسح الباركود (تلقائي)
                {!barcodeEnabled && (
                  <span
                    style={{
                      color: "#ef4444",
                      fontSize: "14px",
                      marginRight: "10px",
                    }}
                  >
                    (معطل - اضغط "حفظ" لتفعيل الباركود)
                  </span>
                )}
              </label>
              <input
                ref={inputRef}
                type="text"
                id="barcode"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder={
                  barcodeEnabled
                    ? "امسح الباركود هنا..."
                    : "اختر الرفوف واضغط حفظ أولاً..."
                }
                autoComplete="off"
                disabled={!barcodeEnabled || loading}
                style={{
                  backgroundColor: !barcodeEnabled ? "#f3f4f6" : "white",
                  cursor: !barcodeEnabled ? "not-allowed" : "text",
                }}
              />
            </div>
          </div>

          <div className="info-box">
            <span className="info-icon">💡</span>
            <p>
              {!barcodeEnabled
                ? "اختر الرفوف أولاً واضغط 'حفظ' لتفعيل مسح الباركود"
                : "امسح الباركود - سيتم تحديث الرفوف تلقائياً عند التأكيد"}
            </p>
          </div>
        </div>

        {/* Scanned Items Table */}
        {scannedItems.length > 0 ? (
          <div className="table-section">
            <div className="table-header">
              <h2>الإدخالات ({scannedItems.length})</h2>
              <span className="total-quantity">
                الكمية الإجمالية:{" "}
                {scannedItems.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            </div>

            <div className="table-container">
              <table className="barcode-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>اسم الصنف</th>
                    <th>الباركود</th>
                    <th>الكمية</th>
                    <th>تاريخ الإدخال</th>
                    <th>آخر مسح</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {scannedItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td className="product-name-cell">{item.product_name}</td>
                      <td className="barcode-cell">{item.barcode}</td>
                      <td>
                        <input
                          type="text"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            handleQuantityChange(item.id, value || "1");
                          }}
                          className="quantity-input"
                        />
                      </td>
                      <td className="time-cell">{item.entryDate}</td>
                      <td className="time-cell">{item.scannedAt}</td>
                      <td>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>لا توجد إدخالات</h3>
            <p>ابدأ بمسح الباركود لإضافة المنتجات</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="cancel-btn"
            onClick={handleCancel}
            disabled={scannedItems.length === 0 || loading}
          >
            حذف الكل
          </button>
          <button
            className="submit-btn"
            onClick={handleConfirm}
            disabled={scannedItems.length === 0 || loading}
          >
            تأكيد الإدخال ({scannedItems.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeEntry;
